import { monthNames } from './salesReportShared';

export const weekly2Number = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const weekly2Section = (report, key) => (
  (report?.sections || []).find((section) => String(section?.key || '').toLowerCase() === key)
);

const weekly2Rows = (section, limit = 18) => (
  (section?.rows || [])
    .filter((row) => {
      const name = String(row?.dimensionValue || '').trim().toUpperCase();
      return name && name !== 'TOTAL' && String(row?.rowType || '').toLowerCase() !== 'total';
    })
    .slice(0, limit)
);

const weekly2CloneRow = (row) => ({
  ...row,
  amountByWeek: { ...(row?.amountByWeek || {}) },
  amountLyByWeek: { ...(row?.amountLyByWeek || {}) },
  amountByQuarterWeek: { ...(row?.amountByQuarterWeek || {}) },
  amountLyByQuarterWeek: { ...(row?.amountLyByQuarterWeek || {}) },
  targetByQuarter: { ...(row?.targetByQuarter || {}) },
});

const weekly2ProductLineRows = (section, limit = 18) => {
  const rows = section?.rows || [];
  const totalRow = rows.find((row) => {
    const name = String(row?.dimensionValue || '').trim().toUpperCase();
    return name === 'TOTAL' || String(row?.rowType || '').toLowerCase() === 'total';
  });
  const details = weekly2Rows(section, limit).filter((row) => {
    const name = weekly2NormalizeText(row?.dimensionValue);
    return name !== 'unclassified';
  }).map(weekly2CloneRow);
  const unclassifiedRows = weekly2Rows(section, rows.length).filter((row) => (
    weekly2NormalizeText(row?.dimensionValue) === 'unclassified'
  )).map(weekly2CloneRow);
  if (unclassifiedRows.length) {
    let others = details.find((row) => weekly2NormalizeText(row?.dimensionValue) === 'others');
    if (!others) {
      others = weekly2BlankAggregate('Others', 'line');
      details.push(others);
    }
    unclassifiedRows.forEach((row) => weekly2Accumulate(others, row));
  }
  if (!totalRow) return details;
  return [{ ...totalRow, dimensionValue: 'By Product Line', rowType: 'total' }, ...details];
};

export const weekly2DisplayRows = (section, limit = 18) => {
  if (section?.key === 'productCategoryTree') return weekly2CategoryTreeRows(section);
  if (section?.key === 'line') return weekly2ProductLineRows(section, limit);
  if (section?.hierarchical) {
    const rows = section.rows || [];
    if (rows.some((row) => row.rowType === 'total')) return rows;
    const total = {
      dimensionValue: 'TOTAL',
      rowType: 'total',
      amountByWeek: {},
      amountLyByWeek: {},
      amountByQuarterWeek: {},
      amountLyByQuarterWeek: {},
      targetByQuarter: {},
      yearTarget: 0,
    };
    rows.filter((row) => row.rowType === 'parent').forEach((row) => {
      Object.entries(row.amountByWeek || {}).forEach(([key, value]) => {
        total.amountByWeek[key] = weekly2Number(total.amountByWeek[key]) + weekly2Number(value);
      });
      Object.entries(row.amountLyByWeek || {}).forEach(([key, value]) => {
        total.amountLyByWeek[key] = weekly2Number(total.amountLyByWeek[key]) + weekly2Number(value);
      });
      Object.entries(row.amountByQuarterWeek || {}).forEach(([key, value]) => {
        total.amountByQuarterWeek[key] = weekly2Number(total.amountByQuarterWeek[key]) + weekly2Number(value);
      });
      Object.entries(row.amountLyByQuarterWeek || {}).forEach(([key, value]) => {
        total.amountLyByQuarterWeek[key] = weekly2Number(total.amountLyByQuarterWeek[key]) + weekly2Number(value);
      });
      Object.entries(row.targetByQuarter || {}).forEach(([key, value]) => {
        total.targetByQuarter[key] = weekly2Number(total.targetByQuarter[key]) + weekly2Number(value);
      });
      total.yearTarget += weekly2Number(row.yearTarget);
    });
    return [total, ...rows];
  }
  return weekly2Rows(section, limit);
};

const weekly2DefaultQuarterGroups = [
  { key: 'q1', label: '26.Q1 Total', targetLabel: '2026Q1 Target', weeks: Array.from({ length: 14 }, (_, index) => index + 1), months: [1, 2, 3] },
  { key: 'q2', label: '26.Q2 Total', targetLabel: '2026Q2 Target', weeks: Array.from({ length: 14 }, (_, index) => index + 14), months: [4, 5, 6] },
  { key: 'q3', label: '26.Q3 Total', targetLabel: '2026Q3 Target', weeks: Array.from({ length: 14 }, (_, index) => index + 27), months: [7, 8, 9] },
  { key: 'q4', label: '26.Q4 Total', targetLabel: '2026Q4 Target', weeks: [...Array.from({ length: 13 }, (_, index) => index + 40), 1], months: [10, 11, 12] },
];

export const weekly2QuarterOptions = [
  { value: 'All', label: 'All' },
  { value: 'q1', label: 'Q1' },
  { value: 'q2', label: 'Q2' },
  { value: 'q3', label: 'Q3' },
  { value: 'q4', label: 'Q4' },
];

export const weekly2QuarterGroups = (meta = {}, selectedQuarter = 'All') => {
  const groups = Array.isArray(meta?.quarterGroups) && meta.quarterGroups.length ? meta.quarterGroups : weekly2DefaultQuarterGroups;
  if (!selectedQuarter || selectedQuarter === 'All') return groups;
  return groups.filter((group) => group.key === selectedQuarter);
};

export const weekly2WeekKey = (week) => `W${String(week).padStart(2, '0')}`;

export const weekly2MonthForWeek = (group, index) => {
  const months = group.months || [];
  if (!months.length) return '';
  if (group.key === 'q1') return monthNames[index <= 4 ? months[0] : index <= 8 ? months[1] : months[2]] || '';
  if (group.key === 'q2') return monthNames[index <= 4 ? months[0] : index <= 8 ? months[1] : months[2]] || '';
  if (group.key === 'q3') return monthNames[index <= 4 ? months[0] : index <= 8 ? months[1] : months[2]] || '';
  return monthNames[index <= 4 ? months[0] : index <= 8 ? months[1] : months[2]] || '';
};

const weekly2QuarterWeekMapName = (field) => (
  field === 'amountLyByWeek' ? 'amountLyByQuarterWeek' : 'amountByQuarterWeek'
);

export const weekly2WeekValue = (row, group, week, field = 'amountByWeek') => {
  const weekKey = weekly2WeekKey(week);
  const quarterWeekKey = group?.key ? `${group.key}:${weekKey}` : '';
  const mapName = weekly2QuarterWeekMapName(field);
  if (quarterWeekKey && row?.[mapName]) {
    return Object.prototype.hasOwnProperty.call(row[mapName], quarterWeekKey) ? row[mapName][quarterWeekKey] : 0;
  }
  return row?.[field]?.[weekKey];
};

const weekly2DaysInMonth = (year, month) => new Date(Number(year) || new Date().getFullYear(), Number(month) || 1, 0).getDate();

export const weekly2QuarterDayCount = (group, year) => (
  (group.months || []).reduce((sum, month) => sum + weekly2DaysInMonth(year, month), 0)
);

const weekly2QuarterWeekDayAllocations = (group, year) => {
  const quarterDays = weekly2QuarterDayCount(group, year);
  const weekCount = group.weeks?.length || 0;
  if (!quarterDays || !weekCount) return [];
  if (weekCount === 1) return [quarterDays];

  const firstMonth = group.months?.[0] || 1;
  const quarterStart = new Date(Number(year) || new Date().getFullYear(), firstMonth - 1, 1);
  const startDay = quarterStart.getDay();
  const firstWeekDays = startDay === 0 ? 1 : 8 - startDay;
  const middleWeekCount = Math.max(weekCount - 2, 0);
  const lastWeekDays = Math.max(0, quarterDays - firstWeekDays - (middleWeekCount * 7));
  return [
    firstWeekDays,
    ...Array.from({ length: middleWeekCount }, () => 7),
    lastWeekDays,
  ];
};

export const weekly2ElapsedQuarterDays = (group, year, weekCount) => {
  const allocations = weekly2QuarterWeekDayAllocations(group, year);
  const count = Math.max(Number(weekCount) || 0, 0);
  return allocations.slice(0, count).reduce((sum, days) => sum + days, 0);
};

export const weekly2QuarterTotal = (row, group, field = 'amountByWeek') => (
  (group.weeks || []).reduce((sum, week) => sum + weekly2Number(weekly2WeekValue(row, group, week, field)), 0)
);

export const weekly2YearTotal = (row, field = 'amountByWeek') => (
  weekly2DefaultQuarterGroups.reduce((sum, group) => sum + weekly2QuarterTotal(row, group, field), 0)
);

export const weekly2PreviousQuarterTotal = (row, group, meta = {}) => {
  const groups = weekly2QuarterGroups(meta, 'All');
  const index = groups.findIndex((item) => item.key === group?.key);
  if (index > 0) return weekly2QuarterTotal(row, groups[index - 1]);
  if (index === 0 && groups.length) return weekly2QuarterTotal(row, groups[groups.length - 1], 'amountLyByWeek');
  return 0;
};

const weekly2BlankAggregate = (dimensionValue, rowType, extra = {}) => ({
  dimensionValue,
  rowType,
  amountByWeek: {},
  amountLyByWeek: {},
  amountByQuarterWeek: {},
  amountLyByQuarterWeek: {},
  targetByQuarter: {},
  yearTarget: 0,
  ...extra,
});

const weekly2Accumulate = (target, source) => {
  Object.entries(source?.amountByWeek || {}).forEach(([key, value]) => {
    target.amountByWeek[key] = weekly2Number(target.amountByWeek[key]) + weekly2Number(value);
  });
  Object.entries(source?.amountLyByWeek || {}).forEach(([key, value]) => {
    target.amountLyByWeek[key] = weekly2Number(target.amountLyByWeek[key]) + weekly2Number(value);
  });
  Object.entries(source?.amountByQuarterWeek || {}).forEach(([key, value]) => {
    target.amountByQuarterWeek[key] = weekly2Number(target.amountByQuarterWeek[key]) + weekly2Number(value);
  });
  Object.entries(source?.amountLyByQuarterWeek || {}).forEach(([key, value]) => {
    target.amountLyByQuarterWeek[key] = weekly2Number(target.amountLyByQuarterWeek[key]) + weekly2Number(value);
  });
  Object.entries(source?.targetByQuarter || {}).forEach(([key, value]) => {
    target.targetByQuarter[key] = weekly2Number(target.targetByQuarter[key]) + weekly2Number(value);
  });
  target.yearTarget = weekly2Number(target.yearTarget) + weekly2Number(source?.yearTarget);
};

const weekly2FixedCategoryRows = [
  { label: 'Furniture', rowType: 'category', level: 0, category: 'Furniture', children: [
    { label: 'Chairs', rowType: 'type', level: 1, category: 'Furniture', type: 'Chairs', children: [
      { label: 'Chair One', rowType: 'model', level: 2, category: 'Furniture', type: 'Chairs', model: 'Chair One' },
      { label: 'Chair One (re)', rowType: 'model', level: 2, category: 'Furniture', type: 'Chairs', model: 'Chair One (re)' },
      { label: 'Chair One HB (re)', rowType: 'model', level: 2, category: 'Furniture', type: 'Chairs', model: 'Chair One HB (re)' },
      { label: 'Chair Zero', rowType: 'model', level: 2, category: 'Furniture', type: 'Chairs', model: 'Chair Zero' },
      { label: 'Sunset & Etc.', rowType: 'model', level: 2, category: 'Furniture', type: 'Chairs', model: 'Sunset & Etc.' },
      { label: 'Accessories', rowType: 'model', level: 2, category: 'Furniture', type: 'Chairs', model: 'Accessories' },
      { label: 'Others', rowType: 'model', level: 2, category: 'Furniture', type: 'Chairs', model: 'Others' },
    ] },
    { label: 'Cots', rowType: 'type', level: 1, category: 'Furniture', type: 'Cots' },
    { label: 'Tables', rowType: 'type', level: 1, category: 'Furniture', type: 'Tables' },
  ] },
  { label: 'Poles', rowType: 'category', level: 0, category: 'Poles' },
  { label: 'Games', rowType: 'category', level: 0, category: 'Games' },
  { label: 'Shelter', rowType: 'category', level: 0, category: 'Shelter' },
  { label: 'Accessory', rowType: 'category', level: 0, category: 'Accessory' },
  { label: 'Apparel', rowType: 'category', level: 0, category: 'Apparel' },
  { label: 'Bags', rowType: 'category', level: 0, category: 'Bags' },
  { label: 'Warmth', rowType: 'category', level: 0, category: 'Warmth' },
  { label: 'Others', rowType: 'category', level: 0, residualCategories: ['Furniture', 'Poles', 'Games', 'Shelter', 'Accessory', 'Apparel', 'Bags', 'Warmth'] },
];

const weekly2NormalizeText = (value) => String(value || '').trim().toLowerCase();

const weekly2MatchesFixedCategory = (leaf, node) => {
  if (node.residualCategories) {
    const excluded = new Set(node.residualCategories.map(weekly2NormalizeText));
    return !excluded.has(weekly2NormalizeText(leaf.categoryValue));
  }
  if (node.category && weekly2NormalizeText(leaf.categoryValue) !== weekly2NormalizeText(node.category)) return false;
  if (node.type && weekly2NormalizeText(leaf.productTypeValue) !== weekly2NormalizeText(node.type)) return false;
  if (node.model && weekly2NormalizeText(leaf.dimensionValue) !== weekly2NormalizeText(node.model)) return false;
  return true;
};

const weekly2BuildFixedCategoryNode = (node, leaves, parentKey = '') => {
  const nodeKey = `${parentKey}/${node.rowType}:${node.label}`;
  const row = weekly2BlankAggregate(node.label, node.rowType, {
    level: node.level,
    nodeKey,
    parentKey: parentKey || undefined,
    hasChildren: Boolean(node.children?.length),
  });
  leaves.filter((leaf) => weekly2MatchesFixedCategory(leaf, node)).forEach((leaf) => weekly2Accumulate(row, leaf));
  const childRows = (node.children || []).flatMap((child) => weekly2BuildFixedCategoryNode(child, leaves, nodeKey));
  return [row, ...childRows];
};

function weekly2CategoryTreeRows(section) {
  const leaves = section?.rows || [];
  const total = weekly2BlankAggregate('By Category', 'total');
  leaves.forEach((leaf) => weekly2Accumulate(total, leaf));
  return [total, ...weekly2FixedCategoryRows.flatMap((node) => weekly2BuildFixedCategoryNode(node, leaves))];
}

export const weekly2Ratio = (current, base) => {
  const cur = weekly2Number(current);
  const ref = weekly2Number(base);
  if (!ref) return null;
  return (cur - ref) / Math.abs(ref);
};
